import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { IScene } from './IScene';
import { Cat } from '../entities/Cat';
import { Ball } from '../entities/Ball';
import { InteractiveObject } from '../entities/InteractiveObject';
import { ParticleEmitter } from '../entities/ParticleEmitter';
import { InputState, Quest, RoomRect } from '../types';
import { sound } from '../utils/Audio';
import {
  BALL_CHAOS_REWARD,
  BALL_CHASE_TIME_LIMIT,
  ROUND_TIME_LIMIT
} from '../constants';

export class GameScene implements IScene {
  public container: Container;

  private cat: Cat;
  private ball: Ball;
  private interactiveObjects: InteractiveObject[] = [];
  private particles: ParticleEmitter;

  private floorGfx: Graphics;
  private wallsGfx: Graphics;
  private decorGfx: Graphics;
  private objectsContainer: Container;

  private input: InputState;
  private quests: Quest[] = [];
  private chaosPercent: number = 0;
  private timeLeft: number = ROUND_TIME_LIMIT;
  private isGameOver: boolean = false;

  private onGameOverCallback: (chaos: number, isWin: boolean) => void;
  private onUpdateHUDCallback: (
    chaos: number,
    timeLeft: number,
    quests: Quest[],
    stamina: number,
    ballChaseTimer: number | null
  ) => void;

  private scratchSoundTimer: number = 0;
  private modalContainer: Container;

  private mapWidth: number = 1000;
  private mapHeight: number = 700;
  private rooms: RoomRect[] = [];

  constructor(
    input: InputState,
    onGameOver: (chaos: number, isWin: boolean) => void,
    onUpdateHUD: (
      chaos: number,
      timeLeft: number,
      quests: Quest[],
      stamina: number,
      ballChaseTimer: number | null
    ) => void
  ) {
    this.container = new Container();
    this.input = input;
    this.onGameOverCallback = onGameOver;
    this.onUpdateHUDCallback = onUpdateHUD;

    this.floorGfx = new Graphics();
    this.wallsGfx = new Graphics();
    this.decorGfx = new Graphics();
    this.objectsContainer = new Container();
    this.particles = new ParticleEmitter();
    this.cat = new Cat();
    this.ball = new Ball(300, 300);

    this.modalContainer = new Container();
    this.modalContainer.visible = false;

    this.container.addChild(this.floorGfx);
    this.container.addChild(this.decorGfx);
    this.container.addChild(this.wallsGfx);
    this.container.addChild(this.objectsContainer);
    this.container.addChild(this.ball.container);
    this.container.addChild(this.particles.container);
    this.container.addChild(this.cat.container);
    this.container.addChild(this.modalContainer);

    this.initRooms();
    this.createModalUI();
  }

  private initRooms() {
    this.rooms = [
      // 1. Living Room (Гостиная) - Top Left
      { name: 'Гостиная', x: 40, y: 40, w: 460, h: 320, floorColor: 0xdfb182 },
      // 2. Kitchen (Кухня) - Bottom Left
      { name: 'Кухня', x: 40, y: 360, w: 460, h: 300, floorColor: 0xdcdde1 },
      // 3. Bedroom (Спальня) - Top Right
      { name: 'Спальня', x: 500, y: 40, w: 460, h: 320, floorColor: 0x82ccdd },
      // 4. Bathroom & Hallway (Ванная) - Bottom Right
      { name: 'Ванная & Прихожая', x: 500, y: 360, w: 460, h: 300, floorColor: 0x60a3bc }
    ];
  }

  public start() {
    this.chaosPercent = 0;
    this.timeLeft = ROUND_TIME_LIMIT;
    this.isGameOver = false;
    this.modalContainer.visible = false;

    this.initQuests();
    this.spawnObjects();
    this.drawApartment();

    // Cat starts in center of living room
    this.cat.setPosition(200, 200);

    // Ball starts in spacious open center of living room, far from walls
    this.ball.reset(360, 220);

    this.onUpdateHUDCallback(
      this.chaosPercent,
      this.timeLeft,
      this.quests,
      this.cat.tygydyk,
      null
    );
    sound.playMeow();
  }

  private initQuests() {
    this.quests = [
      {
        id: 'q_couch',
        title: 'Поточить когти о диван',
        description: 'Диван слишком целый. Исправь это!',
        targetType: 'couch',
        targetCount: 1,
        currentCount: 0,
        completed: false,
        chaosReward: 15
      },
      {
        id: 'q_curtains',
        title: 'Изорвать шторы в клочья',
        description: 'Повиси на красных бархатных шторах',
        targetType: 'curtains',
        targetCount: 1,
        currentCount: 0,
        completed: false,
        chaosReward: 15
      },
      {
        id: 'q_vase',
        title: 'Сбросить посуду со стола',
        description: 'Законы гравитации требуют проверки',
        targetType: 'vase',
        targetCount: 2,
        currentCount: 0,
        completed: false,
        chaosReward: 15
      },
      {
        id: 'q_ball',
        title: 'Поймать убегающий мячик',
        description: 'Догони его за 10 секунд!',
        targetType: 'ball_catch',
        targetCount: 1,
        currentCount: 0,
        completed: false,
        chaosReward: BALL_CHAOS_REWARD
      },
      {
        id: 'q_closet',
        title: 'Вывернуть шкаф с одеждой',
        description: 'Разбросай носки и платья по спальне',
        targetType: 'closet',
        targetCount: 1,
        currentCount: 0,
        completed: false,
        chaosReward: 15
      },
      {
        id: 'q_hairball',
        title: 'Срыгнуть шерсть на ковер',
        description: 'Именно на дорогой персидский ковер',
        targetType: 'hairball_spot',
        targetCount: 1,
        currentCount: 0,
        completed: false,
        chaosReward: 15
      },
      {
        id: 'q_food',
        title: 'Утащить еду с тарелки',
        description: 'Колбаса и рыба созданы для котиков',
        targetType: 'food_plate',
        targetCount: 1,
        currentCount: 0,
        completed: false,
        chaosReward: 10
      },
      {
        id: 'q_box',
        title: 'Погрызть коробку',
        description: 'Коробка лучше любой когтеточки',
        targetType: 'box',
        targetCount: 1,
        currentCount: 0,
        completed: false,
        chaosReward: 10
      },
      {
        id: 'q_toilet_paper',
        title: 'Размотать туалетную бумагу',
        description: 'Преврати ванную в заснеженную равнину',
        targetType: 'toilet_paper',
        targetCount: 1,
        currentCount: 0,
        completed: false,
        chaosReward: 10
      }
    ];
  }

  private spawnObjects() {
    this.interactiveObjects.forEach(obj => {
      this.objectsContainer.removeChild(obj.container);
      obj.container.destroy();
    });
    this.interactiveObjects = [];

    // Living Room (Гостиная)
    const couch = new InteractiveObject('couch', 220, 120, 'Кожаный Диван', '[ПРОБЕЛ] Драть диван', 15);
    const curtains = new InteractiveObject('curtains', 380, 50, 'Бархатные Шторы', '[ПРОБЕЛ] Драть шторы', 15);
    const flower = new InteractiveObject('flower_pot', 100, 80, 'Монстера', '[ПРОБЕЛ] Опрокинуть', 10);
    const box = new InteractiveObject('box', 140, 240, 'Коробка от доставки', '[ПРОБЕЛ] Погрызть', 10);

    // Kitchen (Кухня)
    const vase1 = new InteractiveObject('vase', 180, 480, 'Кружка с кофе', '[ПРОБЕЛ] Сбросить лапой', 10);
    const vase2 = new InteractiveObject('vase', 360, 430, 'Хрустальная Ваза', '[ПРОБЕЛ] Сбросить вазу', 10);
    const food = new InteractiveObject('food_plate', 100, 420, 'Тарелка с колбасой', '[ПРОБЕЛ] Утащить еду', 10);
    const sponge = new InteractiveObject('sponge', 130, 600, 'Кухонная губка', '[ПРОБЕЛ] Разорвать губку', 10);

    // Bedroom (Спальня)
    const rug = new InteractiveObject('hairball_spot', 700, 200, 'Персидский Ковер', '[ПРОБЕЛ] Срыгнуть шерсть', 15);
    const closet = new InteractiveObject('closet', 600, 80, 'Гардероб', '[ПРОБЕЛ] Вывернуть шкаф', 15);
    const laptop = new InteractiveObject('laptop', 860, 100, 'Рабочий Ноутбук', '[ПРОБЕЛ] Потоптаться', 10);

    // Bathroom & Hallway (Ванная и прихожая)
    const tp = new InteractiveObject('toilet_paper', 580, 400, 'Рулон Бумаги', '[ПРОБЕЛ] Размотать', 10);
    const mirror = new InteractiveObject('mirror', 540, 620, 'Зеркало в прихожей', '[ПРОБЕЛ] Напасть на отражение', 10);

    this.interactiveObjects.push(
      couch,
      curtains,
      flower,
      box,
      vase1,
      vase2,
      food,
      sponge,
      rug,
      closet,
      laptop,
      tp,
      mirror
    );

    this.interactiveObjects.forEach(obj => {
      this.objectsContainer.addChild(obj.container);
    });
  }

  private drawApartment() {
    this.floorGfx.clear();
    this.decorGfx.clear();
    this.wallsGfx.clear();

    // Dark background outside
    this.floorGfx.rect(0, 0, this.mapWidth, this.mapHeight);
    this.floorGfx.fill({ color: 0x0f172a });

    // Rooms floor
    this.rooms.forEach(r => {
      this.floorGfx.rect(r.x, r.y, r.w, r.h);
      this.floorGfx.fill({ color: r.floorColor });
    });

    // Outer & Inner Walls
    this.wallsGfx.rect(40, 40, 920, 620);
    this.wallsGfx.stroke({ width: 10, color: 0x2c3e50 });

    // Dividers
    this.wallsGfx.moveTo(500, 40);
    this.wallsGfx.lineTo(500, 170);
    this.wallsGfx.moveTo(500, 240);
    this.wallsGfx.lineTo(500, 480);
    this.wallsGfx.moveTo(500, 550);
    this.wallsGfx.lineTo(500, 660);
    this.wallsGfx.stroke({ width: 8, color: 0x34495e });

    this.wallsGfx.moveTo(40, 360);
    this.wallsGfx.lineTo(240, 360);
    this.wallsGfx.moveTo(320, 360);
    this.wallsGfx.lineTo(500, 360);
    this.wallsGfx.stroke({ width: 8, color: 0x34495e });

    this.wallsGfx.moveTo(500, 360);
    this.wallsGfx.lineTo(700, 360);
    this.wallsGfx.moveTo(780, 360);
    this.wallsGfx.lineTo(960, 360);
    this.wallsGfx.stroke({ width: 8, color: 0x34495e });

    // Furniture decorations
    // Bed
    this.decorGfx.roundRect(760, 60, 160, 110, 8);
    this.decorGfx.fill({ color: 0xffffff });
    this.decorGfx.stroke({ width: 2, color: 0xbdc3c7 });
    this.decorGfx.roundRect(790, 65, 125, 100, 6);
    this.decorGfx.fill({ color: 0x9b59b6 });

    // Kitchen Counter & Sink
    this.decorGfx.rect(50, 580, 440, 70);
    this.decorGfx.fill({ color: 0x95a5a6 });
    this.decorGfx.stroke({ width: 2, color: 0x7f8c8d });
    this.decorGfx.roundRect(80, 595, 60, 40, 4);
    this.decorGfx.fill({ color: 0xbdc3c7 });

    // Bathtub
    this.decorGfx.roundRect(830, 480, 110, 160, 20);
    this.decorGfx.fill({ color: 0xf5f6fa });
    this.decorGfx.stroke({ width: 3, color: 0xdcdde1 });
    this.decorGfx.roundRect(840, 492, 90, 136, 14);
    this.decorGfx.fill({ color: 0x74b9ff, alpha: 0.6 });

    // Hallway Mat
    this.decorGfx.roundRect(520, 625, 70, 30, 4);
    this.decorGfx.fill({ color: 0xc0392b });
  }

  private createModalUI() {
    const bg = new Graphics();
    bg.rect(-240, -180, 480, 360);
    bg.fill({ color: 0x0f172a, alpha: 0.95 });
    bg.stroke({ width: 3, color: 0xf39c12 });
    this.modalContainer.addChild(bg);

    const title = new Text({
      text: 'ИДЕАЛЬНЫЙ ТЫГЫДЫК!',
      style: new TextStyle({
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 28,
        fontWeight: 'bold',
        fill: '#f39c12'
      })
    });
    title.label = 'modalTitle';
    title.anchor.set(0.5);
    title.position.set(0, -120);
    this.modalContainer.addChild(title);

    const rankText = new Text({
      text: 'Звание: Мастер Хаоса',
      style: new TextStyle({
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 20,
        fontWeight: 'bold',
        fill: '#ffffff'
      })
    });
    rankText.label = 'modalRank';
    rankText.anchor.set(0.5);
    rankText.position.set(0, -60);
    this.modalContainer.addChild(rankText);

    const descText = new Text({
      text: 'Все шторы разодраны, кружки разбиты, ковер помечен.',
      style: new TextStyle({
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 15,
        fill: '#c8d6e5',
        align: 'center',
        wordWrap: true,
        wordWrapWidth: 400
      })
    });
    descText.label = 'modalDesc';
    descText.anchor.set(0.5);
    descText.position.set(0, 0);
    this.modalContainer.addChild(descText);

    // Restart button
    const btn = new Container();
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    btn.position.set(0, 85);

    const btnBg = new Graphics();
    btnBg.roundRect(-90, -25, 180, 50, 25);
    btnBg.fill({ color: 0x10ac84 });
    btn.addChild(btnBg);

    const btnLabel = new Text({
      text: 'ЕЩЁ РАЗ ↺',
      style: new TextStyle({
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 18,
        fontWeight: 'bold',
        fill: '#ffffff'
      })
    });
    btnLabel.anchor.set(0.5);
    btn.addChild(btnLabel);

    btn.on('pointerdown', () => {
      this.start();
    });

    this.modalContainer.addChild(btn);
  }

  public update(delta: number) {
    if (this.isGameOver) return;

    // Countdown Timer
    this.timeLeft -= (delta / 60);
    if (this.timeLeft <= 0) {
      this.timeLeft = 0;
      this.finishGame(false);
      return;
    }

    // 1. Movement Physics & Tygydyk Speed
    const currentMaxSpeed = this.cat.getSpeed();
    const accel = this.cat.isTygydyk ? 1.4 : 0.8;
    const friction = 0.82;

    if (this.input.left) this.cat.vx -= accel * delta;
    if (this.input.right) this.cat.vx += accel * delta;
    if (this.input.up) this.cat.vy -= accel * delta;
    if (this.input.down) this.cat.vy += accel * delta;

    this.cat.vx *= Math.pow(friction, delta);
    this.cat.vy *= Math.pow(friction, delta);

    const curSpeed = Math.sqrt(this.cat.vx * this.cat.vx + this.cat.vy * this.cat.vy);
    if (curSpeed > currentMaxSpeed) {
      this.cat.vx = (this.cat.vx / curSpeed) * currentMaxSpeed;
      this.cat.vy = (this.cat.vy / curSpeed) * currentMaxSpeed;
    }

    // Tygydyk particles
    if (this.cat.isTygydyk && curSpeed > 2) {
      if (Math.random() < 0.3) {
        this.particles.burst(this.cat.x, this.cat.y + 10, 0xf1c40f, 2, 'circle');
      }
    }

    // Wall & Bounds Collision
    let nextX = this.cat.x + this.cat.vx * delta;
    let nextY = this.cat.y + this.cat.vy * delta;

    nextX = Math.max(65, Math.min(935, nextX));
    nextY = Math.max(65, Math.min(635, nextY));

    // Internal walls collisions
    if (nextX > 480 && nextX < 520) {
      const inTopDoor = nextY >= 170 && nextY <= 240;
      const inBottomDoor = nextY >= 480 && nextY <= 550;
      if (!inTopDoor && !inBottomDoor) {
        nextX = this.cat.x;
        this.cat.vx = 0;
      }
    }

    if (nextY > 340 && nextY < 380 && nextX < 500) {
      const inDoor = nextX >= 240 && nextX <= 320;
      if (!inDoor) {
        nextY = this.cat.y;
        this.cat.vy = 0;
      }
    }

    if (nextY > 340 && nextY < 380 && nextX > 500) {
      const inDoor = nextX >= 700 && nextX <= 780;
      if (!inDoor) {
        nextY = this.cat.y;
        this.cat.vy = 0;
      }
    }

    this.cat.setPosition(nextX, nextY);

    // 2. Ball Logic & Chase Mini-Game
    this.ball.update(delta, this.cat.x, this.cat.y);

    const distToBall = Math.sqrt(
      Math.pow(this.cat.x - this.ball.x, 2) + Math.pow(this.cat.y - this.ball.y, 2)
    );

    // Trigger ball chase when cat gets near idle ball
    if (this.ball.state === 'idle' && distToBall < 55) {
      this.ball.triggerChase(this.cat.x, this.cat.y, BALL_CHASE_TIME_LIMIT);
      sound.playMeow();
      this.particles.burst(this.ball.x, this.ball.y, 0xff007f, 12, 'circle');
    } else if (this.ball.canBeCaught() && distToBall < 36) {
      // Caught the ball within time limit!
      this.ball.catch();
      sound.playQuestComplete();
      this.particles.burst(this.ball.x, this.ball.y, 0xffd32a, 20, 'square');
      this.chaosPercent = Math.min(100, this.chaosPercent + BALL_CHAOS_REWARD);

      const ballQuest = this.quests.find(q => q.targetType === 'ball_catch' && !q.completed);
      if (ballQuest) {
        ballQuest.currentCount = 1;
        ballQuest.completed = true;
      }

      if (this.chaosPercent >= 100) {
        this.finishGame(true);
      }
    }

    // 3. Interactive Object Detection
    let nearestObj: InteractiveObject | null = null;
    let minDist = 999;

    for (const obj of this.interactiveObjects) {
      if (obj.isDestroyed) {
        obj.setPromptVisible(false);
        continue;
      }

      const dx = this.cat.x - obj.x;
      const dy = this.cat.y - obj.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < obj.interactionRadius) {
        if (dist < minDist) {
          minDist = dist;
          nearestObj = obj;
        }
      } else {
        obj.setPromptVisible(false);
      }
    }

    if (nearestObj) {
      nearestObj.setPromptVisible(true);
    }

    // 4. Action / Mischief Execution
    if (this.input.action && nearestObj) {
      this.cat.isActing = true;
      this.cat.actionType = nearestObj.type;

      this.cat.angle = Math.atan2(nearestObj.y - this.cat.y, nearestObj.x - this.cat.x) - Math.PI / 2;

      const progressAmount = 1.3 * delta;
      const finished = nearestObj.addProgress(progressAmount);

      this.scratchSoundTimer += delta;
      if (this.scratchSoundTimer > 10) {
        this.scratchSoundTimer = 0;
        this.emitActionEffects(nearestObj);
      }

      if (finished) {
        this.handleMischiefComplete(nearestObj);
      }
    } else {
      this.cat.isActing = false;
      this.cat.actionType = null;
    }

    this.cat.update(delta, this.input.sprint);
    this.particles.update(delta);

    const ballTimer = this.ball.state === 'chased' ? this.ball.chaseTimer : null;
    this.onUpdateHUDCallback(
      this.chaosPercent,
      this.timeLeft,
      this.quests,
      this.cat.tygydyk,
      ballTimer
    );
  }

  private emitActionEffects(obj: InteractiveObject) {
    switch (obj.type) {
      case 'couch':
        sound.playScratch();
        this.particles.burst(obj.x + (Math.random() - 0.5) * 40, obj.y, 0xf1f2f6, 4, 'circle');
        break;
      case 'curtains':
        sound.playScratch();
        this.particles.burst(obj.x + (Math.random() - 0.5) * 40, obj.y + 10, 0xeb2f06, 5, 'line');
        break;
      case 'vase':
        sound.playScratch();
        this.particles.burst(obj.x + 10, obj.y + 10, 0x00d2d3, 3, 'square');
        break;
      case 'hairball_spot':
        sound.playHairball();
        this.particles.burst(this.cat.x, this.cat.y, 0xa3743b, 3, 'circle');
        break;
      case 'toilet_paper':
        sound.playPaperUnroll();
        this.particles.burst(obj.x, obj.y + 20, 0xf5f6fa, 5, 'square');
        break;
      case 'flower_pot':
        sound.playScratch();
        this.particles.burst(obj.x, obj.y, 0x27ae60, 4, 'circle');
        break;
      case 'laptop':
        sound.playScratch();
        this.particles.burst(obj.x, obj.y, 0x3498db, 4, 'square');
        break;
      case 'closet':
        sound.playScratch();
        this.particles.burst(obj.x, obj.y + 20, 0xe74c3c, 5, 'square');
        break;
      case 'box':
        sound.playScratch();
        this.particles.burst(obj.x, obj.y, 0xcd853f, 4, 'square');
        break;
      case 'food_plate':
        sound.playScratch();
        this.particles.burst(obj.x, obj.y, 0xe67e22, 4, 'circle');
        break;
      case 'sponge':
        sound.playScratch();
        this.particles.burst(obj.x, obj.y, 0xf1c40f, 6, 'circle');
        break;
      case 'mirror':
        sound.playScratch();
        this.particles.burst(obj.x, obj.y, 0x74b9ff, 4, 'square');
        break;
    }
  }

  private handleMischiefComplete(obj: InteractiveObject) {
    if (obj.type === 'vase') {
      sound.playGlassShatter();
      this.particles.burst(obj.x, obj.y, 0x00d2d3, 16, 'square');
    } else if (obj.type === 'flower_pot') {
      sound.playGlassShatter();
      this.particles.burst(obj.x, obj.y, 0x3d2314, 18, 'circle');
    } else {
      sound.playQuestComplete();
    }

    const quest = this.quests.find(q => q.targetType === obj.type && !q.completed);
    if (quest) {
      quest.currentCount++;
      if (quest.currentCount >= quest.targetCount) {
        quest.completed = true;
        this.chaosPercent = Math.min(100, this.chaosPercent + quest.chaosReward);
      }
    } else {
      this.chaosPercent = Math.min(100, this.chaosPercent + obj.chaosPoints);
    }

    if (this.chaosPercent >= 100) {
      this.finishGame(true);
    }
  }

  private finishGame(isWin: boolean) {
    this.isGameOver = true;

    const modalTitle = this.modalContainer.getChildByLabel('modalTitle') as Text;
    const modalRank = this.modalContainer.getChildByLabel('modalRank') as Text;
    const modalDesc = this.modalContainer.getChildByLabel('modalDesc') as Text;

    if (isWin) {
      sound.playQuestComplete();
      if (modalTitle) modalTitle.text = '👑 ИДЕАЛЬНЫЙ ТЫГЫДЫК!';
      if (modalRank) modalRank.text = 'Звание: Владыка Хаоса (100%)';
      if (modalDesc) modalDesc.text = 'Квартира повержена в прах! Ты поймал мячик, разодрал шторы и диван! Хозяин будет в шоке!';
    } else {
      sound.playMeow();
      if (modalTitle) modalTitle.text = '🚪 ХОЗЯИН ВЕРНУЛСЯ!';
      if (modalRank) modalRank.text = `Шкала хаоса: ${this.chaosPercent}%`;
      if (modalDesc) modalDesc.text = 'Время вышло! Ты успел навести шороху, но часть мебели всё ещё уцелела.';
    }

    this.modalContainer.visible = true;
    this.onGameOverCallback(this.chaosPercent, isWin);
  }

  public resize(width: number, height: number) {
    const scale = Math.min(width / this.mapWidth, height / this.mapHeight, 1.2);
    this.container.scale.set(scale);
    this.container.position.set(
      (width - this.mapWidth * scale) / 2,
      (height - this.mapHeight * scale) / 2
    );

    this.modalContainer.position.set(this.mapWidth / 2, this.mapHeight / 2);
  }

  public destroy() {
    this.interactiveObjects.forEach(o => o.container.destroy());
    this.particles.clear();
    this.ball.container.destroy();
    this.container.destroy({ children: true });
  }
}
